import React from 'react';
import { useTranslation } from 'react-i18next';
import { useDirection } from '@/hooks/useDirection';
import { Box, IconButton, ButtonBase } from '@mui/material';
import { primitiveColors, semanticColors } from '@/theme/tokens';
import {
  XMarkIcon,
  ArrowDownTrayIcon,
  ChatBubbleLeftEllipsisIcon,
} from '@heroicons/react/24/outline';
import BallotIcon from '@/ui-library/BallotIcon';

const ICON_SIZE = 20;

interface BallotMenuProps {
  isOpen: boolean;
  onToggle: () => void;
  onDownload?: () => void;
  onSurvey?: () => void;
  isDownloading?: boolean;
  notificationHeight?: number;
}

const BallotMenu: React.FC<BallotMenuProps> = ({
  isOpen,
  onToggle,
  onDownload,
  onSurvey,
  isDownloading,
  notificationHeight = 0,
}) => {
  const { t } = useTranslation('ballot');
  const dir = useDirection();
  return (
    <ButtonBase
      component="div"
      onClick={onToggle}
      aria-label={isOpen ? t('menu.close') : t('menu.open')}
      sx={{
        position: {
          xs: 'relative', // Mobile: normal flow
          md: 'fixed', // Desktop: fixed positioning
        },
        top: {
          xs: 'auto', // Mobile: normal flow
          md: notificationHeight, // Desktop: offset by notification banner
        },
        ...(dir === 'rtl'
          ? { left: { xs: 'auto', md: 0 }, right: 'auto' }
          : { right: { xs: 'auto', md: 0 } }),
        height: {
          xs: 'auto', // Mobile: auto height
          md: `calc(100vh - ${notificationHeight}px)`, // Desktop: full viewport height minus banner
        },
        width: 35,
        backgroundColor: 'rgb(205, 216, 227)',
        display: 'flex',
        flexDirection: {
          xs: 'row', // Mobile: horizontal layout
          md: 'column', // Desktop: vertical layout
        },
        alignItems: 'center',
        justifyContent: {
          xs: 'center', // Mobile: center horizontally
          md: 'flex-start', // Desktop: align to top
        },
        gap: 2,
        pt: {
          xs: 1, // Mobile: minimal top padding
          md: 2, // Desktop: more top padding
        },
        pb: {
          xs: 1, // Mobile: bottom padding
          md: 0, // Desktop: no bottom padding
        },
        px: {
          xs: 1, // Mobile: horizontal padding
          md: 0, // Desktop: no horizontal padding
        },
        zIndex: {
          xs: 1, // Mobile: normal stacking
          md: 1600, // Desktop: very high z-index to stay above all other elements
        },
        cursor: 'pointer',
        transition: 'all 0.3s ease',
        '& .MuiIconButton-root': {
          transition: 'color 0.3s ease',
        },
        '&:hover': {
          backgroundColor: primitiveColors.gray28,
          '& .MuiIconButton-root': {
            color: 'white',
          },
        },
      }}
    >
      {/* Toggle Icon - Arrow when closed, X when open (overlaid for smooth transition) */}
      <Box sx={{ position: 'relative' }}>
        <IconButton
          tabIndex={-1}
          aria-hidden="true"
          sx={{
            color: primitiveColors.gray22,
            opacity: isOpen ? 0 : 1,
            pointerEvents: 'none',
            transition: 'opacity 0.2s ease',
            '&:hover': { backgroundColor: semanticColors.overlayWhite10 },
          }}
          size="small"
        >
          <BallotIcon size={ICON_SIZE} />
        </IconButton>
        <IconButton
          tabIndex={-1}
          aria-hidden="true"
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            color: primitiveColors.gray22,
            opacity: isOpen ? 1 : 0,
            pointerEvents: 'none',
            transition: 'opacity 0.2s ease',
            '&:hover': { backgroundColor: semanticColors.overlayWhite10 },
          }}
          size="small"
        >
          <XMarkIcon width={ICON_SIZE} height={ICON_SIZE} />
        </IconButton>
      </Box>

      {/* Download Icon - fades in when ballot is open */}
      {onDownload && (
        <IconButton
          title={t('menu.download')}
          aria-label={isDownloading ? t('menu.generatingPdf') : t('menu.downloadAriaLabel')}
          aria-hidden={!isOpen}
          tabIndex={isOpen ? 0 : -1}
          disabled={isDownloading}
          size="small"
          sx={{
            color: primitiveColors.gray22,
            opacity: isOpen ? 1 : 0,
            pointerEvents: isOpen ? 'auto' : 'none',
            transition: 'opacity 0.2s ease',
            '&:hover': {
              backgroundColor: semanticColors.overlayWhite10,
            },
          }}
          onKeyDown={(e) => e.stopPropagation()}
          onClick={(e) => {
            e.stopPropagation();
            onDownload();
          }}
        >
          <ArrowDownTrayIcon width={ICON_SIZE} height={ICON_SIZE} />
        </IconButton>
      )}

      {/* Survey Icon - visible when ballot is open */}
      {onSurvey && (
        <IconButton
          title={t('menu.feedback')}
          aria-label={t('menu.takeSurvey')}
          aria-hidden={!isOpen}
          tabIndex={isOpen ? 0 : -1}
          size="small"
          sx={{
            color: primitiveColors.gray22,
            opacity: isOpen ? 1 : 0,
            pointerEvents: isOpen ? 'auto' : 'none',
            transition: 'opacity 0.2s ease',
            '&:hover': {
              backgroundColor: semanticColors.overlayWhite10,
            },
          }}
          onKeyDown={(e) => e.stopPropagation()}
          onClick={(e) => {
            e.stopPropagation();
            onSurvey();
          }}
        >
          <ChatBubbleLeftEllipsisIcon width={ICON_SIZE} height={ICON_SIZE} />
        </IconButton>
      )}
    </ButtonBase>
  );
};

export default BallotMenu;
